import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_MANDATORY_KEYS, CLIENT_PROFILE_STAGE_KEYS } from '../../common/constants';
import { isStageDisabled } from '../../common/helper';
import { useAppSelector } from '../../common/hooks';
import { IconButton, Typography, BasicButton } from '../../components/atoms';
import { AddInvestmentAccounts } from '../add-investment-accounts';
import { ReactComponent as AccountsStepperIcon } from '../../assets/icons/accountsStepperIcon.svg';
import { getLabelFromAccountType, removeNonNumeric } from '../../utils';
import { HeaderIcon } from './HeaderIcon';
import { IncomeSourcesType, InvestmentSourcesType } from '../../common/types';

import { Card } from '../../components/molecules/Card/Card';
import { formatCurrency } from '../../utils';
import { ReactComponent as EditIcon } from '../../assets/icons/editIcon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/deleteIcon.svg';
import { saveClient, updateClientInvestmentSources } from '../../features/client';
import { useAppDispatch } from '../../common/hooks';
import { TableWithCustomRender } from '../../components/molecules/TableWithCustomRender';
import { Box } from '@mui/material';
import Dayjs from 'dayjs';
import { ConfirmationCentered } from '../../components/molecules';
import { isRecordDataValid } from '../../features/advisorConsole/utils';

export function calculateTotalIncome(incomeSources: IncomeSourcesType[]) {
    let annualamount = 0;
    incomeSources.forEach((item: any) => {
        annualamount += +(item.frequency === 'Annually' ? item.amount : item.amount * 12);
    });
    return annualamount;
}

export function calculateTotalWealthAvailable(items: readonly InvestmentSourcesType[]) {
    return items.map(({ amount }) => amount).reduce((sum, i) => sum + i, 0);
}
export function calculateAnnualContribution(investmentsSources: InvestmentSourcesType[]) {
    let annualContribution = 0;
    investmentsSources.forEach((item: any) => {
        annualContribution += +(item.frequency === 'Annually'
            ? item.recurringContributions
            : item.recurringContributions * 12);
    });
    return annualContribution;
}

export const StepperAccountsCard = () => {
    // const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [openAddFunds, setOpenAddFunds] = useState(false);
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const { orgInfo } = useAppSelector((state) => state.global);
    const investmentAccountType = orgInfo?.investmentAccountType?.[language] || [];
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const { investmentsSources } = adviceClient.accounts;
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState<string>('');
    const [needPrepopulationOnEdit, setNeedPrepopulationOnEdit] = useState(true);

    const dispatch = useAppDispatch();
    // eslint-disable-next-line
    let annualContribution = 0;
    // eslint-disable-next-line
    let amountAccounts = 0;
    investmentsSources.forEach((item) => {
        amountAccounts += +item.amount;
        annualContribution += +(item.frequency === 'Annually'
            ? Number(removeNonNumeric(item.recurringContributions))
            : Number(removeNonNumeric(item.recurringContributions)) * 12);
    });

    const isDisabled = isStageDisabled(adviceClient, CLIENT_PROFILE_STAGE_KEYS[3]);
    const headers_ff = [
        {
            label: t('NAME'),
            key: 'accountName',
            align: 'left' as const,
            width: '22%',
            render: (value: string) => (
                <Typography
                    label={value}
                    variant="body.large"
                    color="neutral.grey.500"
                    sx={{ textAlign: 'left', lineHeight: '36px' }}
                />
            )
        },
        {
            label: t('OWNER'),
            key: 'accountHolder',
            align: 'left' as const,
            width: '11%',
            render: (value: string) => (
                <Typography
                    label={value === 'primary' ? t('CLIENT') : value === 'joint' ? t('TEXT_JOINT') : t('CO-CLIENT')}
                    variant="body.large"
                    color="neutral.grey.500"
                    sx={{ textAlign: 'left', lineHeight: '36px' }}
                />
            )
        },
        {
            label: t('TYPE'),
            key: 'accountType',
            align: 'left' as const,
            width: '11%',
            sortable: true,
            sortEquation: (a: any, b: any, dir: 'asc' | 'desc') =>
                dir === 'asc' ? a.accountType.localeCompare(b.accountType) : b.accountType.localeCompare(a.accountType),
            render: (value: string) => (
                <Typography
                    label={getLabelFromAccountType(value, investmentAccountType)}
                    variant="body.large"
                    color="neutral.grey.500"
                    sx={{ textAlign: 'left', lineHeight: '36px' }}
                />
            )
        },
        {
            label: t('CURENT_BALANCE'),
            key: 'amount',
            align: 'center' as const,
            width: '13%',
            render: (value: number) => (
                <Typography
                    label={formatCurrency(value)}
                    variant="body.large"
                    color="neutral.grey.500"
                    sx={{ textAlign: 'center', lineHeight: '36px' }}
                />
            )
        },
        {
            label: t('LABELS_ANNUAL_CONTRIBUTION'),
            key: 'recurringContributions',
            align: 'center' as const,
            width: '13%',
            render: (value: number, row: any) => (
                <Typography
                    label={formatCurrency(row.frequency === 'Annually' ? value : value * 12 || 0)}
                    variant="body.large"
                    color="neutral.grey.500"
                    sx={{ textAlign: 'center', lineHeight: '36px' }}
                />
            )
        },
        {
            label: t('BEGINING_YEAR'),
            key: 'recurringContributionsStartDate',
            align: 'center' as const,
            width: '13%',
            render: (value: string) => {
                if (!value)
                    return (
                        <Typography
                            label="-"
                            variant="body.large"
                            color="neutral.grey.500"
                            sx={{ textAlign: 'center', lineHeight: '36px' }}
                        />
                    );
                let date = Dayjs(value, 'DD-MM-YYYY', true);
                if (!date.isValid()) date = Dayjs(value);
                return (
                    <Typography
                        label={date.isValid() ? date.format('YYYY') : '-'}
                        variant="body.large"
                        color="neutral.grey.500"
                        sx={{ textAlign: 'center', lineHeight: '36px' }}
                    />
                );
            }
        },
        {
            label: t('ENDING'),
            key: 'recurringContributionsEndDate',
            align: 'center' as const,
            width: '13%',
            render: (value: string) => {
                if (!value)
                    return (
                        <Typography
                            label="-"
                            variant="body.large"
                            color="neutral.grey.500"
                            sx={{ textAlign: 'center', lineHeight: '36px' }}
                        />
                    );
                let date = Dayjs(value, 'DD-MM-YYYY', true);
                if (!date.isValid()) date = Dayjs(value);
                return (
                    <Typography
                        label={date.isValid() ? date.format('YYYY') : '-'}
                        variant="body.large"
                        color="neutral.grey.500"
                        sx={{ textAlign: 'center', lineHeight: '36px' }}
                    />
                );
            }
        },
        {
            label: t('EDIT'),
            key: 'edit',
            align: 'center' as const,
            width: '14%',
            render: (_value: any, row: any) => (
                <Box display="flex" gap={1} justifyContent="center">
                    <IconButton
                        iconName={<EditIcon color="primary" />}
                        bordered={false}
                        onClick={() => handleEditAccount(row.accountId || '')}
                        color="primary"
                    />
                    <IconButton
                        iconName={<DeleteIcon color="primary" />}
                        bordered={false}
                        onClick={() => {
                            setAccountToDelete(row.accountId || '');
                            setDeleteModalOpen(true);
                        }}
                        color="primary"
                    />
                </Box>
            )
        }
    ];

    const handleAddAccount = () => {
        setSelectedAccountId('');
        setDrawerMode('add');
        setOpenAddFunds(true);
    };

    const handleEditAccount = (accountId: string) => {
        setDrawerMode('edit');
        setSelectedAccountId(accountId);
        setOpenAddFunds(true);
        setNeedPrepopulationOnEdit(false);
        console.log('accountId', accountId);
    };

    const handleDeleteAccount = async (accountId: string) => {
        try {
            // Get the existing handleConfirmDelete logic from AddInvestmentAccounts
            // and just copy the core deletion logic here

            const updatedSources = investmentsSources.filter((source) => source.accountId !== accountId);

            // Update client
            const updatedClientData = {
                ...adviceClient,
                accounts: {
                    ...adviceClient.accounts,
                    investmentsSources: updatedSources
                }
            };

            // Dispatch the updates (import these actions)
            await dispatch(saveClient(updatedClientData));
            await dispatch(updateClientInvestmentSources(updatedSources));
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    return (
        <Card onClick={() => {}}>
            <div className="goe-client-stepper-card-inner">
                <div className="goe-client-card-header-container">
                    <div className="goe-client-card-header">
                        <div className="goe-client-icon">
                            <HeaderIcon isDisabled={false} size={40} icon={<AccountsStepperIcon />} />
                        </div>
                        <div className="goe-client-card-header-content">
                            <Typography
                                variant="headers.h4"
                                color="semantic.text.default"
                                label={t('ACCOUNTS_LABEL_TEXT')}
                            />
                            <Typography
                                label={t('WEALTH_SOURCES_INFO_TEXT')}
                                color="semantic.text.muted"
                                variant="body.small"
                            />
                        </div>
                    </div>
                    <div className="goe-client-card-header-buttons">
                        <BasicButton
                            disabled={isDisabled}
                            variant="outlined"
                            sx={{ marginRight: '8px' }}
                            onClick={handleAddAccount}
                        >
                            {t('ADD_ACCOUNTS')}
                        </BasicButton>
                    </div>
                </div>
                {investmentsSources.length > 0 && (
                    <TableWithCustomRender
                        headers={headers_ff}
                        dataSource={investmentsSources}
                        conditionalRowFormat={(row) => {
                            const isValid = isRecordDataValid(row, ACCOUNT_MANDATORY_KEYS, false, true);
                            return !isValid
                                ? {
                                      '&.MuiTableRow-root': {
                                          boxShadow: (t: any) => `inset 0 0 0 2px ${t.palette.error[500]}`
                                      }
                                  }
                                : undefined;
                        }}
                    />
                )}
            </div>
            <AddInvestmentAccounts
                openDrawer={openAddFunds}
                setOpenDrawer={setOpenAddFunds}
                mode={drawerMode}
                selectedAccountId={selectedAccountId}
                needPrepopulationOnEdit={needPrepopulationOnEdit}
            />
            <ConfirmationCentered
                open={deleteModalOpen}
                onConfirmation={() => {
                    handleDeleteAccount(accountToDelete);
                    setDeleteModalOpen(false);
                }}
                onCancellation={() => setDeleteModalOpen(false)}
                onCloseIconClick={() => setDeleteModalOpen(false)}
                title={`${t('DELETE_CONFIRMATION_MODAL_HEADER_TEXT_INVESTMENT')}`}
                content={`${t('DELETE_CONFIRMATION_MODAL_CONTENT_INVESTMENT')}`}
                confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
            />
        </Card>
    );
};
